import { useParams, Link } from 'react-router-dom';
import { useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));
  const chapterListRef = useRef<HTMLDivElement>(null);
  const [toast, setToast] = useState<string | null>(null);

  const scrollToChapters = () => {
    chapterListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 2500);
  };

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="mt-4 font-heading text-xl font-bold text-foreground-800">漫画未找到</h2>
            <p className="mt-2 text-sm text-foreground-500">该漫画可能已下架或不存在</p>
            <Link
              to="/"
              className="mt-6 inline-block px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
    .slice(0, 3);

  const otherComics = comics
    .filter((c) => c.id !== comic.id && c.categorySlug !== comic.categorySlug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    url: `http://txtjly.com/article/${comic.id}`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: '10',
      ratingCount: comic.views,
    },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 虚空韩漫网 | ${comic.author}作品`}
        description={`${comic.title}，${comic.author}创作的${comic.category}，${comic.description.slice(0, 100)}... 高清免费在线阅读，尽在虚空韩漫网。`}
        keywords={`${comic.title},${comic.author},${comic.category},韩漫在线阅读,虚空韩漫网`}
        ogType="book"
        schema={articleSchema}
      />

      <Navbar />

      {/* Toast */}
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] px-6 py-3 bg-foreground-900 text-background-50 rounded-full text-sm font-medium shadow-lg animate-fade-in whitespace-nowrap">
          {toast}
        </div>
      )}

      <main className="pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[
            { label: '首页', path: '/' },
            { label: comic.category, path: `/category/${comic.categorySlug}` },
            { label: comic.title },
          ]} />
        </div>

        {/* Article Detail Section */}
        <section className="py-10 md:py-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10">
              {/* Left: Info */}
              <div className="lg:w-3/5">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-2 h-2 rounded-full bg-foreground-900"></div>
                  <span className="text-sm font-medium text-foreground-600">作品详情</span>
                </div>

                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight">
                  {comic.title}
                </h1>

                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  {comic.description}
                </p>

                <div className="mt-6 flex items-center gap-3">
                  <span className="font-heading text-base font-bold text-foreground-900">
                    {comic.author}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-accent-500 bg-accent-50 px-2 py-1 rounded-full">
                    <i className="ri-verified-badge-fill text-xs"></i>
                    认证作者
                  </span>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-accent-500 font-bold text-xl">{comic.rating}</span>
                    <div className="flex items-center">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${
                            i < Math.floor(comic.rating / 2)
                              ? 'ri-star-fill text-accent-500'
                              : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <span className="text-sm text-foreground-500">
                    {comic.views >= 10000
                      ? `${(comic.views / 10000).toFixed(1)}万人看过`
                      : `${comic.views}人看过`}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === '已完结'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-primary-100 text-primary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Read Button */}
                <div className="mt-8">
                  <button
                    onClick={scrollToChapters}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-primary-500 text-background-50 font-medium rounded-full text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    立即开始阅读 <i className="ri-arrow-right-line"></i>
                  </button>
                </div>

                {/* Chapter List */}
                <div ref={chapterListRef} className="mt-10 scroll-mt-24">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                    章节列表
                  </h3>
                  <div className="space-y-2">
                    {comic.chapters.map((chapter) => (
                      <button
                        key={chapter.id}
                        onClick={() => showToast(`正在加载 ${chapter.title}...`)}
                        className="w-full flex items-center justify-between p-4 bg-background-100 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer group text-left"
                      >
                        <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors">
                          {chapter.title}
                        </span>
                        <span className="text-xs text-foreground-400">
                          更新于 {chapter.updateDate}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Cover */}
              <div className="lg:w-2/5">
                <div className="lg:sticky lg:top-24">
                  <div className="relative w-full max-w-sm mx-auto aspect-[3/4] rounded-2xl overflow-hidden">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - 虚空韩漫网`}
                      title={`${comic.title} - ${comic.author} | 虚空韩漫网`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                      <p className="text-background-50 font-heading text-lg font-bold">
                        {comic.title}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                    同类推荐
                  </h2>
                </div>
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看更多
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Other Categories */}
        {otherComics.length > 0 && (
          <section className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-10">
                换换口味
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {otherComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Reading CTA */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-foreground-950">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50">
              立即开始阅读
            </h2>
            <p className="mt-3 text-sm text-background-50/60">
              打开《{comic.title}》，进入精彩的韩漫世界
            </p>
            <button
              onClick={scrollToChapters}
              className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-primary-500 text-background-50 font-medium rounded-full text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              开始阅读 <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}